import { flatten, groupBy } from "lodash";
import React from "react";

interface IconComponentProps {
  iconKey: string;
  value: number;
  bottom: string | number;
  left?: string | number;
  title: string;
  icon: React.ReactNode;
}

interface IconsContainerProps<T extends Record<string, any>> {
  chartData: T[];
  height: number;
  icons: Record<string, React.ReactNode>;
  showComparison?: boolean;
  iconKeyFormatter?: (key: string) => string;
  valueFormatter?: (value: number | string) => string;
  dateKey?: string;
  iconSpacing?: number;
  className?: string;
  halfAvatarHeight?: number;
}

const IconComponent: React.FC<IconComponentProps> = ({
  iconKey,
  value,
  bottom,
  left,
  title,
  icon
}) => {
  return (
    <div
      key={`${iconKey}-${value}`}
      className="absolute transition-all"
      style={{
        bottom,
        left
      }}
      title={title}
    >
      {icon}
    </div>
  );
};

// Helper function to generate Y-axis ticks
const generateYAxisTicks = (minValue: number, maxValue: number): number[] => {
  const roundedMin = Math.floor(minValue / 5) * 5;
  const roundedMax = Math.ceil(maxValue / 5) * 5;

  const range = roundedMax - roundedMin;
  const step = range / 4;

  return [
    roundedMin,
    roundedMin + step,
    roundedMin + 2 * step,
    roundedMin + 3 * step,
    roundedMax
  ];
};

export function ChartIcons<T extends Record<string, any>>({
  chartData,
  height,
  icons,
  showComparison = true,
  iconKeyFormatter = (key) => key.split("Value")[0],
  valueFormatter = (value) => `${Number(value).toFixed(1)}%`,
  dateKey = "date",
  iconSpacing = 24,
  className = "relative h-full text-secondary",
  halfAvatarHeight = 11
}: IconsContainerProps<T>): React.ReactElement | null {
  if (!chartData || chartData.length === 0) return null;

  const flattenedChartData = flatten(
    chartData.map((item) => {
      return Object.entries(item)
        .filter(([key]) => key !== dateKey && typeof item[key] === "number")
        .map(([_, value]) => Number(value));
    })
  );

  const minValue = Math.min(...flattenedChartData);
  const maxValue = Math.max(...flattenedChartData);
  const yAxisTicks = generateYAxisTicks(minValue, maxValue);
  const lowerBound = 0;
  const upperBound = yAxisTicks[4];

  const calculateHeight = (value: number): string => {
    if (value === 0) {
      return "20px";
    }
    const chartHeight = height;
    const pointY = value - lowerBound;
    const yAxis = upperBound - lowerBound;
    const px = (pointY * chartHeight) / yAxis - halfAvatarHeight + 17;
    return `${px}px`;
  };

  const lastPoint = chartData[chartData.length - 1];

  // Group by values to detect overlaps
  const valueGroups = groupBy(
    Object.entries(lastPoint).filter(([key]) => key !== dateKey),
    ([_, value]) => value
  );

  return (
    <div className={className}>
      {Object.entries(valueGroups).map(([valueStr, entries]) => {
        const value = Number(valueStr);
        const hasOverlap = entries.length > 1;

        return entries.map(([key, _], index) => {
          const iconKey = iconKeyFormatter(key);
          if (!iconKey || (!showComparison && iconKey !== "you")) return null;

          const icon = icons[iconKey];
          if (!icon) return null;

          return (
            <IconComponent
              key={`${iconKey}-${value}-${index}`}
              iconKey={iconKey}
              value={value}
              bottom={calculateHeight(value)}
              left={hasOverlap ? `${index * (iconSpacing / 2)}px` : undefined}
              title={`${iconKey}: ${valueFormatter(value)}`}
              icon={icon}
            />
          );
        });
      })}
    </div>
  );
}
